import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { getTextWidth, useContainerDimensions, useOnClickOutside } from './util';
import './Breadcrumbs.less';

export default function Breadcrumbs({
  breadcrumbNodesJson = '',
  className,
  ...otherProps
}) {
  // useMemo prevents breadcrumbNodes from reparsing an unchanged JSON string
  const breadcrumbNodes = useMemo(() => {
    const nodesArray = JSON.parse(breadcrumbNodesJson);
    if (Array.isArray(nodesArray)) {
      return nodesArray;
    }
    return [];
  }, [breadcrumbNodesJson]);

  const {hostname} = window.location;
  const containerRef = useRef(null);
  const { width: containerWidth } = useContainerDimensions(containerRef);
  const [maxVisible, setMaxVisible] = useState(1); // Start with mobile-friendly default
  const dropdownContainerRef = useRef(null);
  const [showDropdown, setShowDropdown] = useOnClickOutside(dropdownContainerRef, false);

  useLayoutEffect(() => {
    if (
      containerWidth === null ||
      !containerRef.current ||
      breadcrumbNodes.length <= 1
    ) {
      return;
    }
    const container = containerRef.current;

    const icon = container.querySelector(`div[class*='breadcrumbsIcon']`);
    const separator = container.querySelector(`div[class*='breadcrumbsSeparator']`);
    const iconWidth = icon ? icon.offsetWidth : 30;
    const separatorWidth = separator ? separator.offsetWidth : 20;

    const dropdownButtonWidth = dropdownContainerRef.current
      ? dropdownContainerRef.current.offsetWidth
      : 0;

    const { font } = getComputedStyle(container);

    let totalBreadcrumbWidth = iconWidth + dropdownButtonWidth;
    let visibleCount = 0;
    for (let i = 0; i < breadcrumbNodes.length; i += 1){
      const textWidth = getTextWidth(breadcrumbNodes[i].text, font);
      let requiredWidth = totalBreadcrumbWidth + textWidth + separatorWidth;

      // If this is the last item the dropdown may not be displayed
      if (visibleCount === breadcrumbNodes.length - 1) {
        requiredWidth -= dropdownButtonWidth;
      }

      if (requiredWidth > containerWidth) {
        break;
      }

      totalBreadcrumbWidth = requiredWidth;
      visibleCount += 1;
    }

    const newMaxVisible = Math.max(1, visibleCount);

    // Only update if it's different to minimize re-renders
    if (newMaxVisible !== maxVisible) {
      setMaxVisible(newMaxVisible);
    }
  }, [containerWidth, breadcrumbNodes]);

  useLayoutEffect(() => {
    if (
      !showDropdown ||
      !containerRef.current ||
      !dropdownContainerRef.current
    ) {
      return;
    }

    const breadcrumbWidth = containerRef.current.querySelector(
      `div[class*='breadcrumb']`
    ).offsetWidth;
    const dropdownMenu = dropdownContainerRef.current.querySelector(
      `div[class*='breadcrumbsDropdown']`
    );
    const dropdownMenuWidth = dropdownMenu.offsetWidth;

    if (dropdownMenuWidth > breadcrumbWidth) {
      // Position dropdown to display as much text as possible without clipping
      if (dropdownMenuWidth > containerWidth) {
        dropdownMenu.style.maxWidth = `${containerWidth}px`;
        dropdownMenu.style.right = `${breadcrumbWidth - containerWidth}px`;
      } else {
        dropdownMenu.style.right = `${breadcrumbWidth - dropdownMenuWidth}px`;
      }
    }

    // Clean-up inline styles
    // eslint-disable-next-line consistent-return
    return () => {
      dropdownMenu.style = '';
    };
  }, [showDropdown, containerWidth, maxVisible]);

  const links = breadcrumbNodes.map(node => ({
    url: `/resources/${node.path}`,
    display: node.text,
  }));

  // Show first few items and truncate the end
  const shouldTruncate = links.length > maxVisible;
  const visibleLinks = shouldTruncate ? links.slice(0, maxVisible) : links;
  const hiddenLinks = shouldTruncate ? links.slice(maxVisible) : [];

  return (
    <div
      className={clsx('breadcrumbsContainer', className)}
      ref={containerRef}
      {...otherProps}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: links.map(({ url, display }, i) => {
              const listItem = {
                '@type': 'ListItem',
                position: i + 1,
                name: display,
              };

              if (url) {
                listItem.item = `https://${hostname}${url}`;
              }

              return listItem;
            }),
          }),
        }}
      />

      <div className="breadcrumb">
        <div className="breadcrumbsIcon">
          <img
            width="30"
            height="30"
            src="https://cdn.education.com/files/static/content/breadcrumbs_icon.svg"
            alt=""
          />
        </div>

        {visibleLinks.map(({ url, display }, index) => (
          <React.Fragment key={url}>
            {index > 0 && (
              <i className="icon-right-open breadcrumbSeparator" />
            )}
            <a href={url} className={clsx('link', {
                'last': index === links.length - 1
              })}
            >
              {display}
            </a>
          </React.Fragment>
        ))}

        {shouldTruncate && (
          <>
            <i className="icon-right-open breadcrumbSeparator" />
            <div
              className="breadcrumbsDropdownContainer"
              ref={dropdownContainerRef}
            >
              <button
                type="button"
                className="breadcrumbsDropdownButton"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                + {hiddenLinks.length}{' '}
                <i className="icon-down-open downOpen" />
              </button>
              <div className={clsx('breadcrumbsDropdown', {'hidden': !showDropdown})}>
                {hiddenLinks.map(({ url, display }) => (
                  <a
                    key={url}
                    href={url}
                    className="breadcrumbsDropdownItem"
                  >
                    {display}
                  </a>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

Breadcrumbs.propTypes = {
  breadcrumbNodesJson: PropTypes.string,
  className: PropTypes.string,
};

Breadcrumbs.defaultProps = {
  breadcrumbNodesJson: '',
  className: undefined,
}
